import {
  DuplicatedSchedule,
  DuplicatedScheduleStore
} from '@/models/auxiliary/DuplicatedSchedule'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { useReserveStore } from './useReserveStore'

interface DuplicatedScheduleState {
  flowType: 'sameDay' | 'closeDay' | null
  hasError: boolean | null
  info: string
  schedules: DuplicatedScheduleStore[]
  selectedScheduleIds: string[]
  setFlowType(type: 'sameDay' | 'closeDay')
  setHasError(hasError: boolean | null): void
  setInfo(info: string): void
  setSchedules(schedules: DuplicatedScheduleStore[]): void
  setSelectedScheduleIds(ids: string[]): void
  reset(): void
}

type InitialStateStore = Omit<
  DuplicatedScheduleState,
  | 'setFlowType'
  | 'setHasError'
  | 'setInfo'
  | 'setSchedules'
  | 'setSelectedScheduleIds'
  | 'reset'
>

const initialState: InitialStateStore = {
  flowType: null,
  hasError: false,
  info: '',
  schedules: [],
  selectedScheduleIds: []
}

export const useDuplicateScheduleStore = create<DuplicatedScheduleState>()(
  devtools(
    (set) => ({
      ...initialState,
      setFlowType(type) {
        set({ flowType: type }, false, 'setFlowType')
      },
      setHasError(error) {
        set({ hasError: error }, false, 'setHasError')
      },
      setInfo(info) {
        set({ info }, false, 'setInfo')
      },
      setSchedules(schedules) {
        set({ schedules }, false, 'setSchedules')
      },
      setSelectedScheduleIds(ids) {
        set({ selectedScheduleIds: ids }, false, 'setSelectedScheduleIds')
      },
      reset() {
        set({ ...initialState })
      }
    }),
    {
      name: 'useDuplicateScheduleStore'
    }
  )
)

export const convertSchedulesFromError = (
  schedules: DuplicatedSchedule[],
  dataMap
): DuplicatedScheduleStore[] => {
  return schedules.map((schedule) => {
    const { end, id, schedulingId, start, status } = schedule

    return {
      doctorName: dataMap.doctors[schedule.doctorId].name,
      end,
      id,
      schedulingId,
      start,
      status,
      unitName: dataMap.units[schedule.unitId].name
    }
  })
}

export const setDuplicatedData = (
  data,
  reserveExpirationTimeInMinutes?: number
) => {
  const { setFlowType, setInfo, setSchedules } =
    useDuplicateScheduleStore.getState()

  const setReserveExpirationTime =
    useReserveStore.getState().setReserveExpirationTime
  const setReserveCoutdown = useReserveStore.getState().setReserve

  const { dataMap, flowType, schedules } = data
  const [slot] = schedules

  const info =
    flowType === 'sameDay'
      ? dataMap.doctors[slot.doctorId].name
      : dataMap.products[slot.productIds[0]].name

  setFlowType(flowType)
  setInfo(info)
  setSchedules(convertSchedulesFromError(data.schedules, data.dataMap))

  if (reserveExpirationTimeInMinutes) {
    setReserveExpirationTime(reserveExpirationTimeInMinutes)
    setReserveCoutdown()
  }
}
